import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const FormAnalytics = ({ formId, theme }) => {
  const [analytics, setAnalytics] = useState({
    choiceFields: [],
    responsesByDay: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChart, setActiveChart] = useState('pie'); // 'pie' o 'line'

  // Función para limpiar HTML de textos
  const stripHtml = html => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  // Paleta de colores para los gráficos
  const COLORS = [
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#8884D8',
    '#82CA9D',
    '#A4DE6C',
    '#D0ED57',
  ];

  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          process.env.REACT_APP_URL_BASE_BACKEND +
            `/api/forms/${formId}/analytics`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error al obtener datos de análisis: ${response.status}`
          );
        }

        const data = await response.json();
        console.log('Analytics data:', data);

        // Verificar estructura de datos
        if (!data) {
          throw new Error('No se recibieron datos');
        }

        // Validar y establecer valores predeterminados si es necesario
        setAnalytics({
          formId: data.formId || formId,
          formName: data.formName || 'Formulario',
          totalResponses: data.totalResponses || 0,
          choiceFields: data.choiceFields || [],
          responsesByDay: data.responsesByDay || [],
        });
      } catch (error) {
        console.error('Error al obtener datos de análisis:', error);
        setError(`Error al obtener datos de análisis: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (formId) {
      fetchAnalytics();
    }
  }, [formId]);

  // Función para obtener un color para cada opción
  const getColorForIndex = index => {
    return COLORS[index % COLORS.length];
  };

  const renderNoDataMessage = () => (
    <div
      className={`p-4 rounded-lg shadow ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      <div className="py-8 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium">No hay datos disponibles</h3>
        <p className="mt-1 text-sm text-gray-500">
          No se encontraron datos de análisis para este formulario.
        </p>
        {analytics.totalResponses > 0 && (
          <p className="mt-2 text-sm text-yellow-500">
            Hay {analytics.totalResponses} respuestas pero no se pudieron
            asociar con los campos del formulario.
          </p>
        )}
      </div>
    </div>
  );

  const renderPieCharts = () => {
    // Mostrar campos con opciones que tienen al menos una respuesta
    const fieldsWithData = analytics.choiceFields.filter(
      field => field.options && field.options.some(option => option.count > 0)
    );

    if (!fieldsWithData || fieldsWithData.length === 0) {
      return renderNoDataMessage();
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {fieldsWithData.map((field, index) => {
          // Preparar datos para el gráfico
          const pieData = field.options
            .filter(option => option.count > 0)
            .map(option => ({
              name: stripHtml(option.option),
              value: option.count,
            }));

          // Si después de filtrar no hay datos, mostrar mensaje
          if (pieData.length === 0) {
            return (
              <div
                key={index}
                className={`p-4 rounded-lg shadow ${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}
              >
                <h3 className="text-lg font-semibold mb-2">
                  {stripHtml(field.label)}
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  Tipo:{' '}
                  {field.type === 'radio'
                    ? 'opcion unica'
                    : field.type === 'select'
                    ? 'desplegable'
                    : field.type === 'checkbox'
                    ? 'opcion multiple'
                    : field.type}
                </p>
                <p className="text-center py-8">
                  No hay respuestas para este campo
                </p>
              </div>
            );
          }

          // Calcular el total para los porcentajes
          const total = pieData.reduce((sum, item) => sum + item.value, 0);

          return (
            <div
              key={index}
              className={`p-4 rounded-lg shadow ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              }`}
            >
              <h3 className="text-lg font-semibold mb-2">
                {stripHtml(field.label)}
              </h3>
              <p className="text-sm text-gray-500 mb-2">
                Tipo:{' '}
                {field.type === 'radio'
                  ? 'opcion unica'
                  : field.type === 'select'
                  ? 'desplegable'
                  : field.type === 'checkbox'
                  ? 'opcion multiple'
                  : field.type}{' '}
                | Total respuestas: {field.totalResponses || 0}
              </p>

              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) =>
                        `${stripHtml(name)}: ${(percent * 100).toFixed(1)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={getColorForIndex(idx)}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [
                        `${value} respuestas (${((value / total) * 100).toFixed(
                          1
                        )}%)`,
                        name,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4">
                <h4 className="font-medium mb-2">
                  Distribución de respuestas:
                </h4>
                <ul className="space-y-1">
                  {pieData.map((item, idx) => (
                    <li key={idx} className="flex items-center">
                      <span
                        className="w-4 h-4 rounded-full mr-2"
                        style={{ backgroundColor: getColorForIndex(idx) }}
                      ></span>
                      <span>
                        {stripHtml(item.name)}: {item.value} respuestas (
                        {((item.value / total) * 100).toFixed(1)}%)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLineChart = () => {
    if (!analytics.responsesByDay || analytics.responsesByDay.length === 0) {
      return renderNoDataMessage();
    }

    return (
      <div
        className={`p-4 rounded-lg shadow ${
          theme === 'dark' ? 'bg-gray-800' : 'bg-white'
        }`}
      >
        <h3 className="text-lg font-semibold mb-2">Respuestas por día</h3>
        <p className="text-sm text-gray-500 mb-4">
          Total respuestas: {analytics.totalResponses || 0}
        </p>

        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={analytics.responsesByDay}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip
                formatter={value => [`${value} respuestas`, 'Cantidad']}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                name="Respuestas"
                stroke="#8884d8"
                strokeWidth={2}
                dot={{ r: 6 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 bg-blue-400 rounded-full mb-4"></div>
          <div className="text-lg">Cargando datos de análisis...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-600 bg-red-100 rounded-lg">
        <h3 className="font-bold mb-2">Error:</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div
      className={`p-6 ${
        theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-black'
      }`}
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div className="mb-4 md:mb-0">
          <h2 className="text-2xl font-bold">Análisis de Formulario</h2>
          <p className="text-gray-500">
            {analytics.formName} | Total de respuestas:{' '}
            {analytics.totalResponses || 0}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveChart('pie')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeChart === 'pie'
                ? 'bg-blue-600 text-white'
                : theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Gráficos de Torta
          </button>
          <button
            onClick={() => setActiveChart('line')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeChart === 'line'
                ? 'bg-blue-600 text-white'
                : theme === 'dark'
                ? 'bg-gray-700 hover:bg-gray-600'
                : 'bg-gray-200 hover:bg-gray-300'
            }`}
          >
            Respuestas por Día
          </button>
        </div>
      </div>

      {activeChart === 'pie' ? renderPieCharts() : renderLineChart()}
    </div>
  );
};

export default FormAnalytics;
